/** Check if Web Share API with file sharing is available */
export const canShareFiles = (): boolean => {
  return (
    typeof navigator !== "undefined" &&
    "share" in navigator &&
    "canShare" in navigator
  );
};

/** Check if this specific file type can be shared */
export const canShareFileType = (mimeType: string): boolean => {
  if (!canShareFiles()) return false;
  try {
    const file = new File([""], "test", { type: mimeType });
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
};

/** Determine MIME type from file format extension */
export const getMimeType = (format: string): string => {
  const mimeTypes: Record<string, string> = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    pdf: "application/pdf",
    mp4: "video/mp4"
  };
  return mimeTypes[format.toLowerCase()] || "application/octet-stream";
};

/** Check if we should use native share (mobile + supported file type) */
export const shouldUseNativeShare = (format: string): boolean => {
  const isMobile = window.innerWidth <= 640;
  if (!isMobile) return false;

  const mimeType = getMimeType(format);
  return canShareFileType(mimeType);
};

/** Fetch file and share via Web Share API. Returns true on success. */
export const shareFile = async (
  url: string,
  fileName: string,
  mimeType: string
): Promise<boolean> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: mimeType });

    await navigator.share({
      files: [file],
      title: fileName
    });
    return true;
  } catch (error) {
    // User cancelled or share failed — caller should fall back to browser download
    console.warn("Web Share failed:", error);
    return false;
  }
};
